import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { disableGA, initializeGA } from '../utils/analytics';
import './LegalPages.css';
import './CookieConsent.css';

const CookieSettings = () => {
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
  });
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    // Load current preferences
    const consent = localStorage.getItem('cookie_consent');
    if (consent) {
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
      } catch (e) {
        console.error('Failed to parse cookie consent:', e);
      }
    }
  }, []);

  const handleToggleAnalytics = () => {
    setPreferences({
      ...preferences,
      analytics: !preferences.analytics,
    });
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent', JSON.stringify(preferences));
    setSaveSuccess(true);

    // Show success message
    setTimeout(() => {
      setSaveSuccess(false);
    }, 3000);

    if (preferences.analytics) {
      initializeGA();
    } else {
      disableGA();
    }
  };

  const handleAcceptAll = () => {
    const newPrefs = {
      necessary: true,
      analytics: true,
    };
    setPreferences(newPrefs);
    localStorage.setItem('cookie_consent', JSON.stringify(newPrefs));
    setSaveSuccess(true);

    initializeGA();
  };

  const handleRejectAll = () => {
    const newPrefs = {
      necessary: true,
      analytics: false,
    };
    setPreferences(newPrefs);
    localStorage.setItem('cookie_consent', JSON.stringify(newPrefs));
    setSaveSuccess(true);

    disableGA();
  };

  return (
    <div className="legal-page">
      <Helmet>
        <title>Cookie Settings | Tmper</title>
        <meta name="description" content="Manage your cookie preferences for Tmper." />
        <link rel="canonical" href="https://tmper.app/cookies" />
        <meta property="og:title" content="Cookie Settings | Tmper" />
        <meta property="og:description" content="Manage your cookie preferences for Tmper." />
        <meta property="og:url" content="https://tmper.app/cookies" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="legal-container">
        <div className="breadcrumb" style={{ marginBottom: '20px' }}>
          <Link to="/" style={{ color: '#667eea', textDecoration: 'none' }}>← Back to Home</Link>
        </div>

        <h1>Cookie Settings</h1>
        <p className="last-updated">Manage your cookie preferences</p>

        {saveSuccess && (
          <div style={{
            background: '#d4edda',
            border: '1px solid #c3e6cb',
            color: '#155724',
            padding: '12px 20px',
            borderRadius: '6px',
            marginBottom: '20px',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            ✓ Your preferences have been saved.
          </div>
        )}

        <section>
          <h2>About Cookies</h2>
          <p>
            Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience and understand how you use our site.
          </p>
          <p>
            You can control which cookies we use by adjusting your preferences below. Note that some cookies are essential for the website to function properly and cannot be disabled.
          </p>
        </section>

        <section>
          <h2>Your Cookie Preferences</h2>

          <div className="cookie-category" style={{ marginBottom: '20px' }}>
            <div className="cookie-category-header">
              <div className="cookie-category-info">
                <h3>Essential Cookies</h3>
                <span className="cookie-category-badge required">Always Active</span>
              </div>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={true}
                  disabled={true}
                  readOnly
                />
                <span className="cookie-toggle-slider disabled"></span>
              </label>
            </div>
            <p className="cookie-category-description">
              These cookies are necessary for the website to function and cannot be disabled. They store your upload history, browser ID for anonymous tracking, user login status, and other essential settings.
            </p>
            <details style={{ marginTop: '10px' }}>
              <summary style={{ cursor: 'pointer', color: '#667eea', fontWeight: '500' }}>
                View stored data
              </summary>
              <ul style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                <li><strong>browser_id:</strong> Unique identifier for your browser</li>
                <li><strong>uploaded_images:</strong> Your upload history</li>
                <li><strong>google_user:</strong> Google login information (if logged in)</li>
                <li><strong>r2_config:</strong> R2 storage configuration</li>
                <li><strong>cookie_consent:</strong> Your cookie preferences</li>
              </ul>
            </details>
          </div>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <div className="cookie-category-info">
                <h3>Analytics Cookies</h3>
                <span className="cookie-category-badge optional">Optional</span>
              </div>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={handleToggleAnalytics}
                />
                <span className="cookie-toggle-slider"></span>
              </label>
            </div>
            <p className="cookie-category-description">
              These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. We use Google Analytics for this purpose.
            </p>
            <details style={{ marginTop: '10px' }}>
              <summary style={{ cursor: 'pointer', color: '#667eea', fontWeight: '500' }}>
                View details
              </summary>
              <ul style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                <li><strong>Provider:</strong> Google Analytics</li>
                <li><strong>Cookies:</strong> _ga, _gid, _gat</li>
                <li><strong>Purpose:</strong> Analyze website traffic and usage patterns</li>
                <li><strong>Data collected:</strong> Page views, time on site, device type, location (city level)</li>
                <li><strong>Privacy:</strong> IP addresses are anonymized</li>
              </ul>
            </details>
          </div>
        </section>

        <section>
          <h2>Save Your Preferences</h2>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              className="cookie-btn cookie-btn-primary"
              onClick={handleSavePreferences}
              style={{ fontSize: '16px', padding: '12px 24px' }}
            >
              Save My Preferences
            </button>
            <button
              className="cookie-btn cookie-btn-accept"
              onClick={handleAcceptAll}
              style={{ fontSize: '16px', padding: '12px 24px' }}
            >
              Accept All Cookies
            </button>
            <button
              className="cookie-btn cookie-btn-reject"
              onClick={handleRejectAll}
              style={{ fontSize: '16px', padding: '12px 24px' }}
            >
              Reject Optional Cookies
            </button>
          </div>
        </section>

        <section>
          <h2>Additional Information</h2>
          <p>
            For more information about how we use cookies and protect your privacy, please read our{' '}
            <Link to="/privacy" style={{ color: '#667eea' }}>Privacy Policy</Link>.
          </p>
          <p>
            If you have any questions about our use of cookies, please contact us at{' '}
            <a href="mailto:support@tmper.app" style={{ color: '#667eea' }}>support@tmper.app</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default CookieSettings;
